/**
 * PATH: apps/hr/src/pages/employees/employees-list/api-mapping.ts
 * PURPOSE: Maps workforce-api worker responses to HR Hub Employee type
 * WHY: Bridges backend data model and frontend UI needs for the employees list
 */

import type { Employee, EmploymentType } from '../../../types'

export interface WorkerApiResponse {
  id: string
  first_name: string
  last_name: string
  full_name: string
  email: string | null
  mobile: string | null
  role: string
  trade: string | null
  employment_type: string
  is_active: boolean
  simpro_id: number | null
  department?: string | null
  position?: string | null
  hire_date?: string | null
  hourly_rate?: number | null
  created_at?: string | null
  updated_at?: string | null
  user_id?: string | null
}

/**
 * Maps workforce-api worker response to HR Hub Employee type.
 * Updated to use new fields from HR data gap fix.
 */
export function mapWorkerToEmployee(worker: WorkerApiResponse): Employee {
  const roleToPosition: Record<string, string> = {
    installer: 'Installer',
    supervisor: 'Supervisor',
    leading_hand: 'Leading Hand',
    labourer: 'Labourer',
    pm: 'Project Manager',
    admin: 'Administrator',
    scheduler: 'Scheduler',
    apprentice: 'Apprentice',
  }
  
  const employmentTypeMap: Record<string, EmploymentType> = {
    ft: 'full_time',
    pt: 'part_time',
    casual: 'casual',
    subcontractor: 'contractor',
  }
  
  return {
    id: worker.id,
    employeeId: worker.simpro_id ? `SP${worker.simpro_id}` : worker.id.slice(0, 8).toUpperCase(),
    firstName: worker.first_name,
    lastName: worker.last_name,
    email: worker.email || '',
    phone: worker.mobile || '',
    department: worker.department || worker.trade || 'General',
    position: worker.position || roleToPosition[worker.role] || worker.role || 'Team Member',
    employmentType: employmentTypeMap[worker.employment_type] || 'full_time',
    status: worker.is_active ? 'active' : 'inactive',
    hireDate: worker.hire_date || '',
    location: '',
    createdAt: worker.created_at || new Date().toISOString(),
    updatedAt: worker.updated_at || new Date().toISOString(),
    user_id: worker.user_id || undefined,
  }
}
